/**
 * RejectedScreen - Displayed when user's account has been rejected
 *
 * Shown when:
 * - Admin has rejected the user's registration request
 * - User tries to access protected routes after rejection
 *
 * Features:
 * - Clear rejection message
 * - Rejection reason (if provided)
 * - Contact admin instructions
 * - Sign out button
 */

import { useEffect, useState } from 'react';
import { XCircle, LogOut, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface RejectionData {
  rejection_reason: string | null;
  approved_at: string | null;
}

export default function RejectedScreen() {
  const { signOut, user } = useAuth();
  const [rejectionData, setRejectionData] = useState<RejectionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch rejection data
  useEffect(() => {
    const fetchRejectionData = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('rejection_reason, approved_at')
          .eq('user_id', user.id)
          .single();

        if (!error && data) {
          setRejectionData(data);
        }
      } catch (err) {
        console.error('Failed to fetch rejection data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRejectionData();
  }, [user?.id]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy/95 to-steel flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/images/wingmentor-logo.png"
            alt="WingMentor Logo"
            className="h-20 w-auto"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle size={40} className="text-red-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-navy mb-2">Account Not Approved</h1>

          {/* Description */}
          <p className="text-steel mb-6">
            We're sorry, but your registration request has not been approved at this time.
          </p>

          {/* Rejection Reason */}
          {!isLoading && rejectionData?.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800 mb-1">Reason provided:</p>
                  <p className="text-sm text-red-700">{rejectionData.rejection_reason}</p>
                </div>
              </div>
            </div>
          )}

          {/* Rejection Date */}
          {!isLoading && rejectionData?.approved_at && (
            <p className="text-sm text-gray-500 mb-6">
              Decision made on {formatDate(rejectionData.approved_at)}
            </p>
          )}

          {/* Contact Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              If you believe this is an error or would like more information,
              please contact your organization administrator.
            </p>
          </div>

          {/* Sign Out Button */}
          <button
            onClick={handleSignOut}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-white/60 text-sm mt-6">
          WingMentor - Competency Assessment Platform
        </p>
      </div>
    </div>
  );
}
